import React, { memo } from 'react';
import { Link, List, ListItem, ListItemIcon, ListItemText, Typography } from '@mui/material';
import { useTranslation } from 'i18n';
import { makeStyles } from 'tss-react/mui';

import { EventAddressType } from 'components/types/event';
import AddressInfo from 'components/common/AddressInfo';
import IconPin from 'components/svg/icon-pin.svg';
import IconDirection from 'components/svg/icon-direction.svg';
import IconFlash from 'components/svg/icon-flash.svg';
import IconWheelchair from 'components/svg/icon-wheelchair.svg';

const getMapLink = (lat, lng) => `https://www.google.com/maps/search/?api=1&query=${lat},${lng}`;

const useStyles = makeStyles()((theme) => ({
  iconListItem: {
    minWidth: 'unset',
    width: 26,
    paddingRight: 10,
    color: theme.palette.primary.main,
  },
  secondaryIconListItem: {
    minWidth: 'unset',
    width: 26,
    paddingRight: 10,
    color: theme.palette.secondary.main,
  },
  orangeIconListItem: {
    minWidth: 'unset',
    width: 26,
    paddingRight: 10,
    color: theme.palette.custom.orange.main,
  },
  greenIconListItem: {
    minWidth: 'unset',
    width: 26,
    paddingRight: 10,
    color: theme.palette.custom.green.main,
  },
  font500: {
    fontWeight: 500,
  },
  red: {
    '& path': { fill: '#F76E63' },
  },
}));

function EventExtraInfo({ address: { address_additional_info, wheelchair_accessible, lat, lng, ...address } }) {
  const [t] = useTranslation('public');
  const { address_name, address1, address2, city, state, postal_code, country_display } = address;
  const { classes } = useStyles();
  return (
    <List dense disablePadding>
      <ListItem disableGutters alignItems="flex-start">
        <ListItemIcon className={classes.secondaryIconListItem}>
          <IconPin className={classes.red} />
        </ListItemIcon>
        <ListItemText>
          <AddressInfo
            className={classes.font500}
            postalCode={postal_code}
            countryDisplay={country_display}
            addressName={address_name}
            address1={address1}
            address2={address2}
            city={city}
            state={state}
            component={Typography}
            variant="body2"
            color="textSecondary"
          />
        </ListItemText>
      </ListItem>

      {address_additional_info && (
        <ListItem disableGutters>
          <ListItemIcon className={classes.orangeIconListItem}>
            <IconFlash />
          </ListItemIcon>
          <ListItemText>
            <Typography className={classes.font500} variant="body2" color="textSecondary">
              {address_additional_info}
            </Typography>
          </ListItemText>
        </ListItem>
      )}

      {wheelchair_accessible && (
        <ListItem disableGutters>
          <ListItemIcon className={classes.greenIconListItem}>
            <IconWheelchair />
          </ListItemIcon>
          <ListItemText>
            <Typography className={classes.font500} variant="body2" color="textSecondary">
              {t('event.eventExtraInfo.wheelchairAccessible')}
            </Typography>
          </ListItemText>
        </ListItem>
      )}

      <ListItem disableGutters>
        <ListItemIcon className={classes.iconListItem}>
          <IconDirection fontSize="small" />
        </ListItemIcon>
        <ListItemText>
          <Link href={getMapLink(lat, lng)} target="_blank">
            <Typography className={classes.font500} variant="body2">
              {t('event.eventExtraInfo.getDirections')}
            </Typography>
          </Link>
        </ListItemText>
      </ListItem>
    </List>
  );
}

EventExtraInfo.propTypes = {
  address: EventAddressType.isRequired,
};

export default memo(EventExtraInfo);
